/**
 * OCP-551 - single source of truth for resolving a server's tenant-slot
 * cap on the admin UI. The OCP-436 density model gates real placement on
 * `effective_max_tenants`, NOT the static `max_tenants` hardware-class
 * default - and the two are allowed to disagree in EITHER direction: a
 * small demo box can be capped down to 1 (CPU-bound) while a big STRATO
 * host can be raised above its static baseline (dwell headroom). Every
 * surface that renders a "used / total" slot ratio must resolve the
 * denominator through this module instead of reading `max_tenants`
 * directly, or it silently regresses to the OCP-551 bug (a host the
 * scheduler treats as full reads as having free slots).
 *
 * Mirrors the server-side resolution in
 * `apps/api/src/services/vps-servers.ts` (`effectiveCapSql` /
 * `computeCapacities`): effective_max_tenants when computed, else the
 * static baseline clamped by the operator hard cap.
 */

/**
 * Shape of `vps_servers.effective_capacity_inputs` - the traceability blob
 * the capacity job persists so the admin UI can show WHY the effective cap
 * is what it is. Optional fields mirror the backend's own optionality
 * (older persisted blobs, or a binding that carries no probe detail).
 */
export interface EffectiveCapacityInputs {
  evaluatedAt: string;
  binding: 'cpu' | 'ram' | 'uplink' | 'ceiling' | 'hot' | 'static' | 'override';
  reason: string;
  hot: boolean;
  freshProbes: boolean;
  heldByDwell: boolean;
  staticMaxTenants: number;
  effectiveMaxTenants: number;
  dimensions: { cpu: number | null; ram: number; uplink: number | null };
  constants?: {
    idleRamMbPerTenant: number;
    reserveRamMb: number;
    ccuPerCore: number;
    ccuPerMbit: number;
    expectedCcuPerTenant: number;
    hotNicPct?: number;
  };
  probes: {
    hostCpuP95: number | null;
    hostRamP95: number | null;
    nicRxP95Mbits?: number | null;
    nicTxP95Mbits?: number | null;
    nicUtilP95Pct?: number | null;
    nicHot?: boolean;
    sampleCount: number;
    weightedSlots: number;
    tenantsWithTelemetry: number;
    ccuSum: number;
    ramUsedSumMb: number;
    avgRamMbPerTenant: number;
    avgCcuPerTenant: number;
  };
  specs: { cpuCores: number; ramTotalMb: number; uplinkMbits: number; maxTenantsOverride: number | null };
}

/** Human label for each binding dimension - shared by every surface. */
export const BINDING_LABEL: Record<EffectiveCapacityInputs['binding'], string> = {
  cpu: 'CPU-bound',
  ram: 'RAM-bound',
  uplink: 'Uplink-bound',
  ceiling: 'Ceiling-capped',
  hot: 'Hot - gated',
  static: 'Static floor',
  override: 'Operator cap',
};

/** The minimum a caller needs to resolve a server's effective capacity. */
export interface CapacitySource {
  max_tenants: number;
  max_tenants_override?: number | null;
  effective_max_tenants?: number | null;
  effective_capacity_inputs?: EffectiveCapacityInputs | null;
}

export interface ResolvedCapacity {
  /** The cap that actually governs placement - render this as the denominator. */
  effective: number;
  /** The static hardware-class baseline (vps_servers.max_tenants). */
  static: number;
  /** Operator hard cap, or null when none is set. */
  override: number | null;
  /** True when the dynamic model has never produced a value for this host. */
  notYetComputed: boolean;
  /** Why the effective cap is what it is, when known (from effective_capacity_inputs). */
  reason: string | null;
  /** Which dimension is binding the cap, when known. */
  binding: EffectiveCapacityInputs['binding'] | null;
}

/**
 * Resolve the cap that governs tenant placement for a server, plus enough
 * context (static baseline, override, reason, binding) to explain a
 * mismatch to the operator. Falls back to the static baseline (clamped by
 * an operator override, if set) when the model has never evaluated the
 * host - the same fallback the backend's `effectiveCapSql` uses.
 */
export function resolveEffectiveCapacity(server: CapacitySource): ResolvedCapacity {
  const inputs = server.effective_capacity_inputs ?? null;
  const staticCap = inputs?.staticMaxTenants ?? server.max_tenants;
  const override = server.max_tenants_override ?? null;
  const notYetComputed = server.effective_max_tenants == null;
  const effective = server.effective_max_tenants ?? (override != null ? Math.min(staticCap, override) : staticCap);
  return {
    effective,
    static: staticCap,
    override,
    notYetComputed,
    reason: inputs?.reason ?? null,
    binding: inputs?.binding ?? null,
  };
}
